import {
  ApiError,
  apiGet,
  apiPatch,
  apiPost,
  getAxiosErrorData,
} from '@/api/http';
import { storeAccessToken } from '@/utils/localStorage';
import { isRecord } from '@/utils/typeGuards';

import type { InvitedUserDetails, User } from '@/types/user';
import type { AxiosRequestConfig } from 'axios';

const apiUrl = import.meta.env.VITE_API_URL;

/**
 * The login challenge names the authenticator app `totp`, while `MFAStatus.method`
 * names the same thing `app`. Both come from the backend; keep them separate.
 */
export type MFALoginMethod = 'email' | 'totp';

export type AuthTokenResponse = {
  key?: string;
  access_token?: string;
  refresh_token?: string;
  user?: User;
  admin_user_id?: string;
  client_profile_id?: string;
  mfa_required?: boolean;
  /** Set when the user has no stored preference and must pick email or app. */
  mfa_selection_required?: boolean;
  mfa_token?: string;
  mfa_method?: MFALoginMethod;
  /** Present on the email and selection challenges, absent on the TOTP one. */
  user_email?: string;
  /** Grace period: user may skip MFA setup until enforcement. */
  can_postpone?: boolean;
};

export type AuthErrorResponse = {
  status: number;
  data: unknown;
};

export const isAuthErrorResponse = (
  value: unknown,
): value is AuthErrorResponse =>
  typeof value === 'object' &&
  value !== null &&
  'status' in value &&
  'data' in value;

export const isAuthTokenResponse = (
  value: unknown,
): value is AuthTokenResponse =>
  typeof value === 'object' && value !== null && !isAuthErrorResponse(value);

const catchAuthError = (error: unknown): AuthErrorResponse | undefined => {
  if (error instanceof ApiError) {
    return { status: error.status, data: error.data };
  }
  return undefined;
};

export const loginUserRequest = async (
  data: object,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiPost<AuthTokenResponse>(`${apiUrl}rest-auth/login/`, data, {
      headers: {
        'X-Timezone-Offset': (new Date().getTimezoneOffset() / 60).toString(),
      },
    });
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const loginAsUser = async (
  userId?: string,
  clientProfileId?: string,
  config?: AxiosRequestConfig,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  const payload = clientProfileId
    ? { client_profile_id: clientProfileId }
    : { user: userId };
  try {
    return await apiPost<AuthTokenResponse>(
      `${apiUrl}rest-auth/login-as-user/`,
      payload,
      config,
    );
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const logoutUserRequest = async (): Promise<
  AuthErrorResponse | undefined
> => {
  try {
    await apiPost(`${apiUrl}rest-auth/logout/`);
    return undefined;
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const confirmLoginWithGoogleRequest = async (
  query: string,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiGet<AuthTokenResponse>(
      `${apiUrl}rest-auth/social/google/server/login/callback/${query}`,
    );
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const getUserInfo = async (): Promise<
  User | AuthErrorResponse | undefined
> => {
  try {
    return await apiGet<User>(`${apiUrl}rest-auth/user/`);
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const getInvitedUserDetails = (
  uid: string,
  token: string,
  profileId?: string | null,
) => {
  const url = `${apiUrl}users/invite/details/${uid}/${token}/`;
  const params = profileId ? { params: { profile_id: profileId } } : undefined;
  return apiGet<InvitedUserDetails>(url, params);
};

export const sendResetPassword = (email: string) =>
  apiPost<{ detail: string }>(`${apiUrl}rest-auth/password/reset/`, { email });

export const confirmResetPassword = (data: {
  uid: string;
  token: string;
  new_password: string;
}) =>
  apiPost<{ detail: string }>(
    `${apiUrl}rest-auth/password/reset/confirm/`,
    data,
  );

export const changePassword = async (data: {
  old_password: string;
  new_password: string;
}) => {
  const response = await apiPost<{ detail: string; key?: string }>(
    `${apiUrl}rest-auth/password/change/`,
    data,
  );
  const newKey = response?.key;
  if (newKey) {
    storeAccessToken(newKey);
  }
  return response;
};

export const getUserByMagicLink = (
  magic_link: string,
  client_profile_id?: string,
) =>
  apiPost<AuthTokenResponse>(`${apiUrl}rest-auth/magic-link/login/`, {
    magic_link,
    ...(client_profile_id ? { client_profile_id } : {}),
  });

export const confirmAccountByMagicLink = (
  magic_link: string,
  client_profile_id?: string,
) =>
  apiPost<AuthTokenResponse>(`${apiUrl}rest-auth/magic-link/account-confirm/`, {
    magic_link,
    ...(client_profile_id ? { client_profile_id } : {}),
  });

export const updateUser = (data: Partial<User>) =>
  apiPatch<User>(`${apiUrl}rest-auth/user/`, data);

export const updateUserLanguage = (default_language: string) =>
  apiPatch<{ default_language: string }>(`${apiUrl}rest-auth/user/language/`, {
    default_language,
  });

export const updateUserTimezone = (default_timezone: string | null) =>
  apiPatch<{ default_timezone: string | null }>(
    `${apiUrl}rest-auth/user/timezone/`,
    { default_timezone },
  );

export const getLanguageUpdateErrorMessage = (
  error: unknown,
): string | undefined => {
  const data = getAxiosErrorData(error);
  if (!isRecord(data)) return undefined;
  const errors = data.errors;
  if (isRecord(errors)) {
    const nested = errors.errors;
    if (isRecord(nested) && typeof nested.default_language === 'string') {
      return nested.default_language;
    }
  }
  if (typeof data.message === 'string') return data.message;
  return undefined;
};
